import React from "react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Skeleton,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/DownloadOutlined";
import HistoryEduOutlinedIcon from "@mui/icons-material/HistoryEduOutlined";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";

import useLog from "../../../../hooks/super/useLog";
import { formatDate } from "../../../../utils/format";
import { getComparator, stableSort } from "../../../../utils/tableSort";
import ViewLog from "./M_ViewLog";
import {
  TOKENS,
  Monogram,
  RowMenu,
  EmptyState,
} from "../../../../components/pages/_new/super/design";

const headCells = [
  { id: "date", label: "Timestamp", sortable: true },
  { id: "action", label: "Action", sortable: true },
  { id: "username", label: "User", sortable: true },
  { id: "organisation", label: "Organisation", sortable: true },
  { id: "ip_addr", label: "IP Address", sortable: true },
  { id: "actions", label: "", sortable: false },
];

const HeaderCell = ({ children, sortable, id, orderBy, order, onSort, align }) => (
  <TableCell
    align={align || "left"}
    sx={{
      padding: "10px 14px",
      borderBottom: `1px solid ${TOKENS.border}`,
      background: TOKENS.bgSunken,
      color: TOKENS.fg2,
      fontSize: 11.5,
      fontWeight: 600,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
    }}
  >
    {sortable ? (
      <TableSortLabel
        active={orderBy === id}
        direction={orderBy === id ? order : "asc"}
        onClick={() => onSort(id)}
        sx={{
          color: `${TOKENS.fg2} !important`,
          "& .MuiTableSortLabel-icon": { color: `${TOKENS.fg2} !important` },
        }}
      >
        {children}
      </TableSortLabel>
    ) : (
      children
    )}
  </TableCell>
);

const DataCell = ({ children, align }) => (
  <TableCell
    align={align || "left"}
    sx={{
      padding: "12px 14px",
      borderBottom: `1px solid ${TOKENS.borderSubtle}`,
      color: TOKENS.fg1,
      fontSize: 13.5,
    }}
  >
    {children}
  </TableCell>
);

function LogList() {
  const { logs, getLogs, getLog, total_logs_count, from, size } = useLog();
  const [order, setOrder] = React.useState("desc");
  const [orderBy, setOrderBy] = React.useState("date");
  const [page, setPage] = React.useState(from / size);
  const [rowsPerPage, setRowsPerPage] = React.useState(size);
  const [log, setLog] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const [viewOpen, setViewOpen] = React.useState(false);
  const [actionKeyStr, setActionKeyStr] = React.useState("");
  const [userKeyStr, setUserKeyStr] = React.useState("");
  const [ipAddrKeyStr, setIpAddrKeyStr] = React.useState("");

  React.useEffect(() => {
    setPage(from / size);
  }, [from, size]);
  React.useEffect(() => {
    setRowsPerPage(size);
  }, [size]);

  const handleSort = (id) => {
    if (id === orderBy) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setOrderBy(id);
      setOrder("asc");
    }
  };

  const getConditions = () => {
    const conditions = [];
    if (actionKeyStr) conditions.push({ key: "action", condition: "cont", value: actionKeyStr });
    if (userKeyStr) conditions.push({ key: "username", condition: "cont", value: userKeyStr });
    if (ipAddrKeyStr) conditions.push({ key: "ip_addr", condition: "cont", value: ipAddrKeyStr });
    return conditions;
  };
  const onSearchClick = () => {
    setPage(0);
    getLogs(rowsPerPage, 0, false, getConditions());
  };

  const viewClick = async (logID) => {
    setLoading(true);
    setViewOpen(true);
    const l = await getLog(logID);
    setLog(l);
    setLoading(false);
  };

  const download = () => {
    const curDate = new Date();
    const input = document.getElementById("auditLogList");
    if (!input) return;
    html2canvas(input).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: input.offsetWidth > input.offsetHeight ? "l" : "p",
        unit: "px",
        format: [input.offsetWidth + 240, input.offsetHeight + 180],
      });
      pdf.addImage(imgData, "JPEG", 120, 90, input.offsetWidth, input.offsetHeight);
      pdf.save(`SG_AuditLog (` + formatDate(curDate) + `).pdf`);
    });
  };

  const sorted = React.useMemo(
    () => stableSort(Array.isArray(logs) ? logs : [], getComparator(order, orderBy)),
    [logs, order, orderBy]
  );

  const isLoading = logs === null;
  const isEmpty = !isLoading && sorted.length === 0;
  const last = Math.max(0, Math.ceil(total_logs_count / rowsPerPage) - 1);

  return (
    <>
      <Box
        id="auditLogList"
        sx={{
          border: `1px solid ${TOKENS.border}`,
          borderRadius: `${TOKENS.radiusM}px`,
          background: TOKENS.bgSurface,
          boxShadow: TOKENS.shadowXs,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: "14px 16px",
            borderBottom: `1px solid ${TOKENS.borderSubtle}`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Action contains..."
            value={actionKeyStr}
            onChange={(e) => setActionKeyStr(e.target.value)}
            sx={{
              minWidth: 200,
              "& .MuiOutlinedInput-root": { borderRadius: "10px", background: TOKENS.bgSurface },
            }}
          />
          <TextField
            size="small"
            placeholder="User contains..."
            value={userKeyStr}
            onChange={(e) => setUserKeyStr(e.target.value)}
            sx={{
              minWidth: 200,
              "& .MuiOutlinedInput-root": { borderRadius: "10px", background: TOKENS.bgSurface },
            }}
          />
          <TextField
            size="small"
            placeholder="IP contains..."
            value={ipAddrKeyStr}
            onChange={(e) => setIpAddrKeyStr(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: TOKENS.fg3 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 200,
              "& .MuiOutlinedInput-root": { borderRadius: "10px", background: TOKENS.bgSurface },
            }}
          />
          <Typography
            component="button"
            type="button"
            onClick={onSearchClick}
            sx={{
              border: `1px solid ${TOKENS.border}`,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: TOKENS.bgSurface,
              color: TOKENS.fg1,
              fontSize: 12.5,
              fontWeight: 600,
              px: 1.5,
              py: 0.5,
              cursor: "pointer",
              "&:hover": { background: TOKENS.bgSunken },
            }}
          >
            Apply
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: 12.5, color: TOKENS.fg3 }}>
            {isLoading ? "Loading..." : `${sorted.length} of ${total_logs_count}`}
          </Typography>
          <IconButton size="small" onClick={download} sx={{ color: TOKENS.fg2 }}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Box>

        <TableContainer>
          <Table sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                {headCells.map((c) => (
                  <HeaderCell
                    key={c.id}
                    id={c.id}
                    sortable={c.sortable}
                    orderBy={orderBy}
                    order={order}
                    onSort={handleSort}
                    align={c.id === "actions" ? "right" : "left"}
                  >
                    {c.label}
                  </HeaderCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: rowsPerPage || 5 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {headCells.map((__, j) => (
                      <DataCell key={`sk-${i}-${j}`}>
                        <Skeleton variant="rectangular" height={18} sx={{ borderRadius: 1 }} />
                      </DataCell>
                    ))}
                  </TableRow>
                ))
              ) : isEmpty ? (
                <TableRow>
                  <TableCell colSpan={headCells.length} sx={{ borderBottom: "none" }}>
                    <EmptyState
                      icon={<HistoryEduOutlinedIcon />}
                      title="No audit entries"
                      body="Activity will appear here as administrators take action."
                    />
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((row, idx) => {
                  const actions = [
                    {
                      key: "view",
                      icon: <RemoveRedEyeIcon fontSize="small" />,
                      label: "View details",
                      onClick: () => viewClick(row?.id),
                    },
                  ];
                  return (
                    <TableRow
                      key={`${row?.id}-${idx}`}
                      hover
                      sx={{ "&:hover": { background: TOKENS.bgSunken } }}
                    >
                      <DataCell>
                        <Typography sx={{ fontSize: 12.5, color: TOKENS.fg1 }}>
                          {row?.date ? formatDate(row.date) : "—"}
                        </Typography>
                      </DataCell>
                      <DataCell>
                        <Typography sx={{ fontSize: 13, color: TOKENS.fg1, fontWeight: 500 }}>
                          {row?.action || "—"}
                        </Typography>
                      </DataCell>
                      <DataCell>
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                          <Monogram name={row?.username || "?"} size={28} radius={8} />
                          <Typography sx={{ fontSize: 13, color: TOKENS.fg1 }}>
                            {row?.username || "—"}
                          </Typography>
                        </Stack>
                      </DataCell>
                      <DataCell>
                        <Typography sx={{ fontSize: 13, color: TOKENS.fg2 }}>
                          {row?.organisation || "—"}
                        </Typography>
                      </DataCell>
                      <DataCell>
                        <Typography
                          sx={{
                            fontSize: 12.5,
                            color: TOKENS.fg1,
                            fontFamily:
                              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                          }}
                        >
                          {row?.ip_addr || "—"}
                        </Typography>
                      </DataCell>
                      <DataCell align="right">
                        <RowMenu actions={actions} />
                      </DataCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!isLoading && total_logs_count > 0 ? (
          <Box
            sx={{
              p: "10px 16px",
              borderTop: `1px solid ${TOKENS.borderSubtle}`,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontSize: 12.5, color: TOKENS.fg2 }}>
              Page {page + 1} of {Math.max(1, last + 1)}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography
              component="button"
              type="button"
              onClick={() => {
                if (page > 0) {
                  setPage(page - 1);
                  getLogs(rowsPerPage, rowsPerPage * (page - 1), false, getConditions());
                }
              }}
              disabled={page === 0}
              sx={{
                border: `1px solid ${TOKENS.border}`,
                borderRadius: `${TOKENS.radiusXs}px`,
                background: TOKENS.bgSurface,
                color: page === 0 ? TOKENS.fg3 : TOKENS.fg1,
                fontSize: 12.5,
                fontWeight: 600,
                px: 1.5,
                py: 0.5,
                cursor: page === 0 ? "not-allowed" : "pointer",
              }}
            >
              Prev
            </Typography>
            <Typography
              component="button"
              type="button"
              onClick={() => {
                if (page < last) {
                  setPage(page + 1);
                  getLogs(rowsPerPage, rowsPerPage * (page + 1), false, getConditions());
                }
              }}
              disabled={page >= last}
              sx={{
                border: `1px solid ${TOKENS.border}`,
                borderRadius: `${TOKENS.radiusXs}px`,
                background: TOKENS.bgSurface,
                color: page >= last ? TOKENS.fg3 : TOKENS.fg1,
                fontSize: 12.5,
                fontWeight: 600,
                px: 1.5,
                py: 0.5,
                cursor: page >= last ? "not-allowed" : "pointer",
              }}
            >
              Next
            </Typography>
            <Box
              component="select"
              value={rowsPerPage}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                setRowsPerPage(v);
                setPage(0);
                getLogs(v, 0, false, getConditions());
              }}
              sx={{
                border: `1px solid ${TOKENS.border}`,
                borderRadius: `${TOKENS.radiusXs}px`,
                background: TOKENS.bgSurface,
                color: TOKENS.fg1,
                fontSize: 12.5,
                px: 1,
                py: 0.5,
              }}
            >
              <option value={5}>5 / page</option>
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
            </Box>
          </Box>
        ) : null}
      </Box>
      <ViewLog open={viewOpen} handleClose={() => setViewOpen(false)} log={log} loading={loading} />
    </>
  );
}

export default LogList;
